import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import {
  answerComment,
  type ChecklistAnswers,
  type ChecklistQuestion,
  type ChecklistSchema,
} from '@/api/checklists.api';

type Props = {
  schema: ChecklistSchema;
  answers: ChecklistAnswers;
  /** Hide the Answer column when reviewing a blank template. */
  hideAnswers?: boolean;
};

/** Single-cell rendering of an answer — mirrors the server's formatAnswer(). */
export function formatAnswer(question: ChecklistQuestion, value: unknown): string {
  if (value === null || value === undefined || value === '') return '';
  if (Array.isArray(value)) return value.join(', ');
  if (question.type === 'rating') return `${value} / ${question.max ?? 5}`;
  return String(value);
}

/**
 * Spreadsheet-style rendering of a checklist — the "Table" half of the review
 * toggle. Grid lines and a section band per group, matching the register
 * tables users already read in Algebra, and column-for-column identical to
 * the XLSX export so the download holds no surprises.
 */
export function ChecklistTableView({ schema, answers, hideAnswers }: Props) {
  const { t } = useTranslation();
  let n = 0;
  const colCount = hideAnswers ? 5 : 7;

  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            <Th className="w-12 text-center">#</Th>
            <Th className="w-40">{t('checklist.col_section')}</Th>
            <Th>{t('checklist.col_question')}</Th>
            <Th className="w-36">{t('checklist.col_type')}</Th>
            <Th className="w-24 text-center">{t('checklist.col_required')}</Th>
            {!hideAnswers && <Th className="w-56">{t('checklist.col_answer')}</Th>}
            {!hideAnswers && <Th className="w-64">{t('checklist.col_comment')}</Th>}
          </tr>
        </thead>
        <tbody>
          {schema.sections.map((section) => (
            <Fragment key={section.id}>
              <tr className="bg-primary-50/60">
                <td
                  colSpan={colCount}
                  className="border border-border px-3 py-2 font-semibold text-primary"
                >
                  {section.title}
                  {section.description && (
                    <span className="ml-2 font-normal text-muted">— {section.description}</span>
                  )}
                </td>
              </tr>
              {section.questions.map((q) => {
                n += 1;
                const text = formatAnswer(q, answers[q.id]);
                return (
                  <tr key={q.id} className="hover:bg-[#FAFBFC]">
                    <Td className="text-center text-muted">{n}</Td>
                    <Td className="text-muted">{section.title}</Td>
                    <Td className="text-gray-900">
                      {q.label}
                      {q.help && <div className="text-[12px] text-muted mt-0.5">{q.help}</div>}
                    </Td>
                    <Td className="text-muted">{t(`checklist.type.${q.type}`)}</Td>
                    <Td className="text-center">
                      {q.required ? <span className="badge badge-warning">{t('checklist.yes')}</span> : ''}
                    </Td>
                    {!hideAnswers && (
                      <Td className={text ? 'text-gray-900' : 'text-muted'}>
                        {text || <span className="italic">{t('checklist.no_answer')}</span>}
                      </Td>
                    )}
                    {!hideAnswers && (
                      <Td className="whitespace-pre-wrap text-gray-700">
                        {q.allowComment ? answerComment(q.id, answers) : ''}
                      </Td>
                    )}
                  </tr>
                );
              })}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={`border border-border bg-[#F3F6F9] px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-muted ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <td className={`border border-border px-3 py-2 align-top ${className}`}>{children}</td>
  );
}
